import type { RangeStats } from '../workoutStream';

export interface ComparisonSideMetrics {
  durationSec?: number | null;
  distanceM?: number | null;
  avgPower?: number | null;
  maxPower?: number | null;
  np?: number | null;
  tss?: number | null;
  vi?: number | null;
  avgHr?: number | null;
  maxHr?: number | null;
  avgCadence?: number | null;
  avgSpeed?: number | null;
  elevationGain?: number | null;
}

export interface ComparisonDeltaRow {
  key: string;
  label: string;
  unit: string;
  valueA: number | null;
  valueB: number | null;
  /** Human-readable value for side A (e.g. h:mm:ss, km). */
  valueAFormatted: string;
  /** Human-readable value for side B, formatted identically to side A. */
  valueBFormatted: string;
  delta: number | null;
  deltaFormatted: string;
  higherIsBetter: boolean;
}

/** Seconds → h:mm:ss (matches calendar / workout detail). */
export function formatDurationHMS(seconds: number): string {
  const s = Math.max(0, Math.round(Math.abs(seconds)));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

function formatDurationDelta(deltaSec: number): string {
  const sign = deltaSec >= 0 ? '+' : '-';
  return `${sign}${formatDurationHMS(deltaSec)}`;
}

/** Metres → km label. */
export function formatDistanceKm(meters: number): string {
  const km = meters / 1000;
  return `${km >= 100 ? km.toFixed(1) : km.toFixed(2)} km`;
}

function formatDistanceDelta(deltaM: number): string {
  const sign = deltaM >= 0 ? '+' : '-';
  const km = Math.abs(deltaM) / 1000;
  return `${sign}${km >= 100 ? km.toFixed(1) : km.toFixed(2)} km`;
}

function fmtDelta(delta: number | null, unit: string, decimals = 0): string {
  if (delta == null || !isFinite(delta)) return '—';
  const sign = delta >= 0 ? '+' : '';
  const val = decimals > 0 ? delta.toFixed(decimals) : String(Math.round(delta));
  return `${sign}${val}${unit ? ` ${unit}` : ''}`;
}

function fmtNumericValue(value: number, decimals = 0): string {
  return decimals > 0 ? value.toFixed(decimals) : String(Math.round(value));
}

interface RowFormatOptions {
  decimals?: number;
  formatValue?: (value: number) => string;
  formatDelta?: (delta: number) => string;
}

function row(
  key: string,
  label: string,
  unit: string,
  a: number | null | undefined,
  b: number | null | undefined,
  higherIsBetter: boolean,
  options: RowFormatOptions = {},
): ComparisonDeltaRow | null {
  const { decimals = 0, formatValue, formatDelta } = options;
  const valueA = a != null && isFinite(a) ? a : null;
  const valueB = b != null && isFinite(b) ? b : null;
  if (valueA == null && valueB == null) return null;
  const delta = valueA != null && valueB != null ? valueA - valueB : null;
  const valueFormatter = formatValue ?? ((v: number) => fmtNumericValue(v, decimals));
  return {
    key,
    label,
    unit,
    valueA,
    valueB,
    valueAFormatted: valueA != null ? valueFormatter(valueA) : '—',
    valueBFormatted: valueB != null ? valueFormatter(valueB) : '—',
    delta,
    deltaFormatted:
      delta != null && formatDelta
        ? formatDelta(delta)
        : fmtDelta(delta, unit, decimals),
    higherIsBetter,
  };
}

/** Build delta grid from side metrics (full workout or segment). */
export function computeComparisonDeltas(
  metricsA: ComparisonSideMetrics,
  metricsB: ComparisonSideMetrics,
): ComparisonDeltaRow[] {
  const rows: (ComparisonDeltaRow | null)[] = [
    row('duration', 'Duration', '', metricsA.durationSec, metricsB.durationSec, false, {
      formatValue: formatDurationHMS,
      formatDelta: formatDurationDelta,
    }),
    row('distance', 'Distance', '', metricsA.distanceM, metricsB.distanceM, true, {
      formatValue: formatDistanceKm,
      formatDelta: formatDistanceDelta,
    }),
    row('avg_power', 'Avg Power', 'W', metricsA.avgPower, metricsB.avgPower, true),
    row('max_power', 'Max Power', 'W', metricsA.maxPower, metricsB.maxPower, true),
    row('np', 'NP', 'W', metricsA.np, metricsB.np, true),
    row('tss', 'TSS', '', metricsA.tss, metricsB.tss, false),
    row('vi', 'Variability', '', metricsA.vi, metricsB.vi, false, { decimals: 2 }),
    row('avg_hr', 'Avg HR', 'bpm', metricsA.avgHr, metricsB.avgHr, false),
    row('max_hr', 'Max HR', 'bpm', metricsA.maxHr, metricsB.maxHr, false),
    row('avg_cadence', 'Avg Cadence', 'rpm', metricsA.avgCadence, metricsB.avgCadence, true),
    row('avg_speed', 'Avg Speed', 'km/h', metricsA.avgSpeed, metricsB.avgSpeed, true, { decimals: 1 }),
    row('elevation', 'Elev Gain', 'm', metricsA.elevationGain, metricsB.elevationGain, true),
  ];
  return rows.filter((r): r is ComparisonDeltaRow => r != null);
}

/** Map client-side range stats to ComparisonSideMetrics. */
export function rangeStatsToMetrics(stats: RangeStats | null, distanceM?: number | null): ComparisonSideMetrics {
  if (!stats) return {};
  return {
    durationSec: stats.duration,
    distanceM: distanceM ?? null,
    avgPower: stats.power?.avg ?? null,
    maxPower: stats.power?.max ?? null,
    avgHr: stats.heartRate?.avg ?? null,
    maxHr: stats.heartRate?.max ?? null,
    avgCadence: stats.cadence?.avg ?? null,
    avgSpeed: stats.speed?.avg ?? null,
  };
}

/** Map API calculateSelectedRange response to ComparisonSideMetrics. */
export function extractSelectedRangeCalculation(apiBody: unknown): Record<string, unknown> | null {
  if (!apiBody || typeof apiBody !== 'object') return null;
  const root = apiBody as Record<string, unknown>;
  const envelope = root.data;
  if (envelope && typeof envelope === 'object') {
    const calc = (envelope as Record<string, unknown>).calculation;
    if (calc && typeof calc === 'object') return calc as Record<string, unknown>;
    return envelope as Record<string, unknown>;
  }
  const direct = root.calculation;
  if (direct && typeof direct === 'object') return direct as Record<string, unknown>;
  return root;
}

function num(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  return isFinite(n) ? n : null;
}

/** True when parsed metrics contain no usable comparison values. */
export function comparisonMetricsAreEmpty(metrics: ComparisonSideMetrics): boolean {
  return (
    metrics.durationSec == null &&
    metrics.distanceM == null &&
    metrics.avgPower == null &&
    metrics.np == null &&
    metrics.tss == null &&
    metrics.avgHr == null &&
    metrics.maxPower == null
  );
}

/** Map API calculateSelectedRange response to ComparisonSideMetrics. */
export function apiRangeToMetrics(data: Record<string, unknown> | null): ComparisonSideMetrics {
  if (!data) return {};
  const mma = data.min_max_avg as Record<string, { avg?: number; max?: number }> | undefined;
  const np = num(data.np ?? data.normalizedPower);
  const tss = num(data.tss_actual ?? data.tss);
  const avgPower = num(mma?.power?.avg ?? data.avgPower ?? data.avg_power);
  const avgHr = num(mma?.heart_rate?.avg ?? data.avgHeartRate ?? data.avg_heart_rate);
  return {
    durationSec: num(data.total_time ?? data.totalTime),
    distanceM: num(data.distance),
    avgPower: avgPower ?? np,
    maxPower: num(mma?.power?.max),
    np,
    tss,
    vi: num(data.vi),
    avgHr,
    maxHr: num(mma?.heart_rate?.max),
    avgCadence: num(mma?.cadence?.avg),
    avgSpeed: num(mma?.speed?.avg),
    elevationGain: num(data.total_ascent),
  };
}
